import { useCallback, useEffect, useState } from 'react';

const useNeedsScroll = (
  ref: React.RefObject<HTMLElement>,
  isVisible: boolean
) => {
  const [needsScroll, setNeedsScroll] = useState(false);

  useEffect(() => {
    if (!isVisible) return;

    const timer = setTimeout(() => {
      const element = ref.current;
      if (!element) return;

      const checkForScroll = () => {
        setNeedsScroll(element.scrollHeight > element.clientHeight);
      };

      const resizeObserver = new ResizeObserver(checkForScroll);
      resizeObserver.observe(element);

      return () => resizeObserver.disconnect();
    }, 0);

    return () => clearTimeout(timer);
  }, [ref, isVisible]);

  return needsScroll;
};

export interface ScrollSpyElement {
  elementRef: React.RefObject<HTMLDivElement>;
  title: string;
  id: string;
}

export interface ScrollSpyState {
  scrolledElement: string | undefined;
  needsScrollSpy: boolean;
  handleScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  scrollToRef: (ref: React.RefObject<HTMLDivElement>) => void;
}

export const useScrollSpyState = (
  scrollContainerRef: React.RefObject<HTMLDivElement>,
  isElementVisible: boolean,
  elements?: ScrollSpyElement[],
  scrollOffset?: number // allows the spy to scroll a bit above the top of the element, in case you have a sticky header or something
) => {
  const [scrolledElement, setScrolledElement] = useState<string | undefined>(
    elements?.[0]?.id
  );

  useEffect(() => {
    setScrolledElement(elements?.[0]?.id);
    // don't want to do this every time the reference changes, just the number of elements
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements?.length]);

  const needsScrollSpy = useNeedsScroll(scrollContainerRef, isElementVisible);

  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (Math.abs(scrollHeight - clientHeight - scrollTop) < 1) {
        setScrolledElement(elements?.[elements.length - 1]?.id);
      } else {
        elements?.forEach((element, index) => {
          const adjustedTop = scrollTop + (scrollOffset ?? 0);
          if (
            adjustedTop >= (element.elementRef.current?.offsetTop ?? 0) &&
            (!elements[index + 1] ||
              adjustedTop <
                (elements[index + 1].elementRef.current?.offsetTop ?? 0))
          ) {
            setScrolledElement(element.id);
          }
        });
      }
    },
    [elements]
  );

  const scrollToRef = useCallback(
    (ref: React.RefObject<HTMLDivElement>) => {
      if (ref.current && scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          top:
            ref.current.offsetTop -
            scrollContainerRef.current.offsetTop -
            (scrollOffset ?? 0),
          behavior: 'smooth',
        });
      }
    },
    [scrollContainerRef]
  );

  return {
    scrolledElement,
    needsScrollSpy,
    handleScroll,
    scrollToRef,
  };
};
